import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Draggable } from '@hello-pangea/dnd'
import cx from 'helpers/cx'

import { DeleteSmallIcon } from '@pleio/icons/delete-small'
import { MoveVerticalIcon } from '@pleio/icons/move-vertical'

import FormItem from 'js/components/Form/FormItem'
import IconButton from 'js/components/IconButton'

import styles from './ColophonField.module.css'

type ColophonFieldProps = {
    id: string
    index: number
    onRemove: () => void
}

const ColophonField = ({ id, index, onRemove }: ColophonFieldProps) => {
    const { t } = useTranslation()

    const { control } = useFormContext()

    const watchColophon = useWatch({
        name: 'colophonFields',
        control,
    })

    const label = watchColophon?.[index]?.label

    const buttonProps = {
        type: 'button',
        size: 'large',
        variant: 'secondary',
        bgOffset: 'sm',
        radiusStyle: 'rounded',
    } as const

    const ariaLabel = `${index + 1}. ${label}`

    return (
        <Draggable draggableId={id} index={index}>
            {(provided, snapshot) => (
                <li
                    ref={provided.innerRef}
                    className={cx(
                        styles.wrapper,
                        snapshot.isDragging && styles.isDragging,
                    )}
                    {...provided.draggableProps}
                >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            {...buttonProps}
                            as="div"
                            tooltip={t('action.move')}
                            aria-label={t('aria.move-field', {
                                label: ariaLabel,
                            })}
                            {...(provided.dragHandleProps as any)}
                            tooltipProps={{ side: 'left' }}
                        >
                            <MoveVerticalIcon />
                        </IconButton>

                        <FormItem
                            control={control}
                            type="text"
                            name={`colophonFields.${index}.label`}
                            label={t('global.label')}
                            className={styles.colophonFieldLabel}
                        />

                        <IconButton
                            {...buttonProps}
                            onClick={onRemove}
                            aria-label={t('aria.remove-field', {
                                label: ariaLabel,
                            })}
                            tooltip={t('action.delete')}
                            tooltipProps={{ side: 'right' }}
                        >
                            <DeleteSmallIcon />
                        </IconButton>
                    </div>
                </li>
            )}
        </Draggable>
    )
}

export default ColophonField
